<template>
  <div class="environment-selector">
    <label for="env-select">Environment:</label>
    <select id="env-select" v-model="selectedEnvironment" @change="switchEnvironment" :disabled="disabled">
      <option v-for="(config, key) in environments" :key="key" :value="key">
        {{ key.toUpperCase() }} - {{ getDataSourceName(key) }}{{ getReadonlyStatus(key) }}
      </option>
    </select>
    <span class="env-indicator" :class="`env-${selectedEnvironment}`">{{ selectedEnvironment.toUpperCase() }}</span>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useEnvironmentStore } from '../stores/environment';
import { useAuth } from '../composables/useAuth';
import { AVAILABLE_ENVIRONMENTS, isEnvironmentEnabled } from '../utils/environmentConfig';

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false
  }
});

// Use Pinia store for environment state
const environmentStore = useEnvironmentStore();
const { currentEnvironment, environments: allEnvironments } = storeToRefs(environmentStore);

const selectedEnvironment = ref('dev');
const { isAdmin, isDeployment, isDeveloper, isReadonly } = useAuth();

const environments = computed(() => {
  const filtered = {};
  
  // Get available environments from build-time configuration
  const availableEnvs = AVAILABLE_ENVIRONMENTS.map(env => env.value);
  
  // Filter based on user permissions and build-time configuration
  Object.keys(allEnvironments.value).forEach(key => {
    // Only include environments that are enabled in build configuration
    if (!availableEnvs.includes(key)) return;
    
    // Admin and deployment can see all enabled environments
    if (isAdmin.value || isDeployment.value) {
      filtered[key] = allEnvironments.value[key];
      return;
    }
    
    // Developer can see all enabled environments (but readonly in UAT/LIVE)
    if (isDeveloper.value) {
      filtered[key] = allEnvironments.value[key];
      return;
    }
    
    // Readonly can only see dev and test (if enabled)
    if (key === 'dev' || key === 'test') {
      filtered[key] = allEnvironments.value[key];
    }
  });
  
  return filtered;
});

// Watch for store changes and sync to local state
watch(currentEnvironment, (newEnv) => {
  selectedEnvironment.value = newEnv;
}, { immediate: true });

onMounted(() => {
  // Initialize from store
  selectedEnvironment.value = currentEnvironment.value;
});

const getDataSourceName = (env) => {
  const envVarName = `VITE_DB_NAME_${env.toUpperCase()}`;
  return import.meta.env[envVarName] || env;
};

const getReadonlyStatus = (env) => {
  // Explicit readonly role gets readonly on all environments
  if (isReadonly.value && !isDeveloper.value) return ' (readonly)';
  
  // Developer in UAT/LIVE is readonly
  if (isDeveloper.value && (env === 'uat' || env === 'live')) return ' (readonly)';
  
  return '';
};

const switchEnvironment = () => {
  // Use Pinia store action to switch environment
  environmentStore.switchEnvironment(selectedEnvironment.value);
};
</script>

<style scoped>
.environment-selector {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  background: var(--bg-color);
  border: 1px solid var(--border-color);
  border-radius: 4px;
}

label {
  font-weight: bold;
  color: var(--text-color);
}

select {
  padding: 5px 10px;
  border: 1px solid var(--border-color);
  border-radius: 3px;
  background: var(--input-bg);
  color: var(--text-color);
  min-width: 200px;
}

select:disabled {
  background-color: #f5f5f5;
  color: #6c757d;
  cursor: not-allowed;
}

.env-indicator {
  padding: 4px 8px;
  border-radius: 3px;
  font-size: 12px;
  font-weight: bold;
}

.env-dev { background: #28a745; color: white; }
.env-test { background: #ffc107; color: black; }
.env-uat { background: #fd7e14; color: white; }
.env-live { background: #dc3545; color: white; }
</style>